import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  clearWorkflowTags,
  fetchWorkflowTagsBootstrap,
  useAppDispatch,
  useAppSelector,
} from '../store';
import { isQcLiteE2eBypass } from '../utils/qcLiteE2eBypass';

const WorkflowTagsContext = createContext(null);

const E2E_MOCK_AUTH_SUB = 'e2e|mock-user';

/**
 * @typedef {{ tag_id: number, tag_name: string, workflow_id: string }} WorkflowTagRow
 */

/**
 * @returns {{
 *   tagsByWorkflowId: Record<string, WorkflowTagRow[]>,
 *   workflowIds: string[],
 *   globalWorkflows: Record<string, unknown>[],
 *   version: number | null,
 *   updatedAt: string | null,
 *   isLoading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<unknown>,
 *   getTagsForWorkflow: (workflowId: string | undefined) => WorkflowTagRow[],
 *   getAllTagsUnique: () => WorkflowTagRow[],
 * }}
 */
export function useWorkflowTags() {
  const ctx = useContext(WorkflowTagsContext);
  if (!ctx) {
    throw new Error('useWorkflowTags must be used within WorkflowTagsProvider');
  }
  return ctx;
}

export function useWorkflowTagsOptional() {
  return useContext(WorkflowTagsContext);
}

export function WorkflowTagsProvider({ children }) {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const bypass = isQcLiteE2eBypass();
  const authSub = bypass ? E2E_MOCK_AUTH_SUB : user?.sub ?? '';

  const tagsByWorkflowId = useAppSelector(
    (state) => state.workflowTags.tagsByWorkflowId,
  );
  const workflowIds = useAppSelector((state) => state.workflowTags.workflowIds);
  const globalWorkflows = useAppSelector(
    (state) => state.workflowTags.globalWorkflows,
  );
  const version = useAppSelector((state) => state.workflowTags.version);
  const updatedAt = useAppSelector((state) => state.workflowTags.updatedAt);
  const isLoading = useAppSelector((state) => state.workflowTags.isLoading);
  const error = useAppSelector((state) => state.workflowTags.error);

  const runBootstrap = useCallback(() => {
    if (!authSub) return Promise.resolve();
    // RTK thunks expose `.unwrap()` which rejects on error; attach a catch so
    // the `error` is surfaced via Redux state without triggering an
    // `unhandledrejection`. Callers awaiting `refetch` still get the thunk
    // action object.
    const action = dispatch(fetchWorkflowTagsBootstrap());
    if (action && typeof action.unwrap === 'function') {
      action.unwrap().catch((err) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[WorkflowTagsProvider] bootstrap failed', err);
        }
      });
    }
    return action;
  }, [authSub, dispatch]);

  useEffect(() => {
    if (!bypass && authLoading) return;

    if (!bypass && !isAuthenticated) {
      dispatch(clearWorkflowTags());
      return;
    }

    if (!authSub) return;

    try {
      runBootstrap();
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[WorkflowTagsProvider] bootstrap threw', err);
      }
    }
  }, [
    bypass,
    isAuthenticated,
    authLoading,
    authSub,
    runBootstrap,
    dispatch,
  ]);

  const getTagsForWorkflow = useCallback(
    (workflowId) => {
      if (workflowId === null || workflowId === undefined || workflowId === '')
        return [];
      const key = String(workflowId);
      return tagsByWorkflowId[key] ?? [];
    },
    [tagsByWorkflowId],
  );

  const getAllTagsUnique = useCallback(() => {
    const map = new Map();
    Object.values(tagsByWorkflowId).forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((row) => {
        if (row && row.tag_id !== undefined) {
          map.set(row.tag_id, row);
        }
      });
    });
    return [...map.values()];
  }, [tagsByWorkflowId]);

  const value = useMemo(
    () => ({
      tagsByWorkflowId,
      workflowIds,
      globalWorkflows,
      version,
      updatedAt,
      isLoading,
      error,
      refetch: runBootstrap,
      getTagsForWorkflow,
      getAllTagsUnique,
    }),
    [
      tagsByWorkflowId,
      workflowIds,
      globalWorkflows,
      version,
      updatedAt,
      isLoading,
      error,
      runBootstrap,
      getTagsForWorkflow,
      getAllTagsUnique,
    ],
  );

  return (
    <WorkflowTagsContext.Provider value={value}>
      {children}
    </WorkflowTagsContext.Provider>
  );
}
